/**
 * useSMARTLaunch — handles SMART on FHIR session bootstrap.
 *
 * On mount:
 * - If mode === 'mock' or 'extension': skip, use mock patient ID from URL.
 * - If mode === 'smart': use the access token already obtained by
 *   SmartCallback.tsx (stored in localStorage) to set up the session.
 *   Does NOT use fhirclient library — our SmartLaunch/SmartCallback
 *   pages handle the full OAuth2 flow manually.
 */
import { useEffect } from 'react';
import { useSession } from '../context/SessionContext';
import type { LaunchContext } from '../types';

const API_BASE = import.meta.env.VITE_API_BASE ?? '/raapid-api';

export function useSMARTLaunch() {
  const { launch, setLaunch, setLoading, setError } = useSession();

  useEffect(() => {
    if (launch.mode !== 'smart') return;

    // Block data fetching until we've resolved the correct patient ID.
    setLoading(true);

    const accessToken = localStorage.getItem('smart_access_token');
    const fhirUrl = localStorage.getItem('smart_fhir_url');

    if (!accessToken) {
      setError('No SMART access token found. Please relaunch from the EHR.');
      setLoading(false);
      return;
    }

    async function bootstrap() {
      // Determine the FHIR patient UUID.
      // Priority: URL ?patientId= param → localStorage smart_last_patient → fetch from FHIR.
      // The default 'CRN-PAT-00482' in raapidPatientId means no real UUID was provided.
      const DEFAULT_ID = 'CRN-PAT-00482';
      const urlPatientId = launch.raapidPatientId !== DEFAULT_ID ? launch.raapidPatientId : null;
      let fhirUuid = urlPatientId || localStorage.getItem('smart_last_patient') || '';

      // If we still don't have a UUID, ask the FHIR server who the current patient is.
      if (!fhirUuid && fhirUrl) {
        try {
          const ptRes = await fetch(`${fhirUrl}/Patient?_count=1`, {
            headers: { Authorization: `Bearer ${accessToken}`, Accept: 'application/fhir+json' },
          });
          if (ptRes.ok) {
            const bundle = await ptRes.json();
            fhirUuid = bundle.entry?.[0]?.resource?.id ?? '';
            if (fhirUuid) localStorage.setItem('smart_last_patient', fhirUuid);
          }
        } catch { /* non-fatal */ }
      }

      let resolvedPatientId = fhirUuid;
      if (fhirUuid) {
        try {
          const res = await fetch(`${API_BASE}/v1/patients/fhir/${encodeURIComponent(fhirUuid)}`);
          if (res.ok) {
            const result: { patientId: string } = await res.json();
            resolvedPatientId = result.patientId ?? fhirUuid;
          }
        } catch {
          // Non-fatal — mock API fallback will handle unknown IDs
        }
      }

      const updatedCtx: LaunchContext = {
        ...launch,
        fhirPatientId: fhirUuid || null,
        raapidPatientId: resolvedPatientId || DEFAULT_ID,
        smartClient: { accessToken, fhirUrl } as unknown as LaunchContext['smartClient'],
      };

      setLaunch(updatedCtx);
      setLoading(false);
    }

    bootstrap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
